import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import Navbar from "./components/NavbarComponent";
import Home from "./components/Home";
import PatientDetails from "./components/PatientDetails";
import Login from "./components/Login";
import Register from "./components/Register";
import ErrorBoundary from "./components/ErrorBoundary";
import { DEFAULTS } from "./constants/app";
import { auth } from "./utils";

export default function App() {
  // Initialize state from localStorage using utility functions
  const [isLoggedIn, setIsLoggedIn] = useState(() => {
    const loggedIn = auth.isLoggedIn();
    return loggedIn;
  });
  const [userEmail, setUserEmail] = useState(() => auth.getUserEmail());
  const [isAdmin, setIsAdmin] = useState(() => auth.isAdmin());
  const [hospitalName, setHospitalName] = useState(() =>
    auth.getHospitalName()
  );
  const [userId, setUserId] = useState(() => auth.getUserId());

  const navigate = useNavigate();
  const location = useLocation();

  const handleLogin = (
    email: string,
    role: 'admin' | 'super_admin',
    hospitalNameParam?: string,
    userIdParam?: string,
    token?: string
  ) => {
    const displayName = hospitalNameParam || DEFAULTS.HOSPITAL_NAME;
    const userIdValue = userIdParam || "";
    const isAdminUser = role === 'super_admin'; // Calculate from role

    // Update state
    setIsLoggedIn(true);
    setUserEmail(email);
    setIsAdmin(isAdminUser);
    setHospitalName(displayName);
    setUserId(userIdValue);

    // Save to localStorage using utility (now accepts token)
    auth.saveUserSession(email, role, displayName, userIdValue, token);
    
    // Add a delay to ensure cookie is processed by browser before navigation
    // This is especially important for cross-origin cookies in incognito mode
    // Increased delay to give browser more time to process the cookie
    setTimeout(() => {
      navigate("/");
    }, 300);
  };

  const handleLogout = () => {
    // Clear state
    setIsLoggedIn(false);
    setUserEmail("");
    setIsAdmin(false);
    setHospitalName("");
    setUserId("");

    // Clear localStorage using utility
    auth.clearUserSession();
    navigate("/");
  };

  // Prevent browser back navigation when logged out
  useEffect(() => {
    window.history.pushState(null, "", "/");
  }, [isLoggedIn, location.pathname]);

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-gray-100">
        <Routes>
          <Route path="/register" element={<Register />} />
          <Route
            path="/"
            element={
              !isLoggedIn ? (
                <Login onLogin={handleLogin} />
              ) : (
                <div className="min-h-screen bg-gray-100">
                  <Navbar
                    isLoggedIn={true}
                    userName={hospitalName}
                    onLogout={handleLogout}
                  />
                  <Home
                    userEmail={userEmail}
                    isAdmin={isAdmin}
                    userId={userId}
                  />
                </div>
              )
            }
          />
          <Route
            path="/patient/:id"
            element={
              !isLoggedIn ? (
                <Login onLogin={handleLogin} />
              ) : (
                <div className="min-h-screen bg-gray-100">
                  <Navbar
                    isLoggedIn={true}
                    userName={hospitalName}
                    onLogout={handleLogout}
                  />
                  <PatientDetails />
                </div>
              )
            }
          />
          <Route
            path="*"
            element={<div>Route not found: {location.pathname}</div>}
          />
        </Routes>
      </div>
    </ErrorBoundary>
  );
}
